import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
  InputLabel,
  Alert,
} from "@mui/material";
import { ChevronBigLeft, CloseSmall, InfoInfo } from "@tiny/icons";
import { Controller, FormProvider } from "react-hook-form";
import useConditionalSplitForm from "./ConditionalSplitForm.hooks";
import AudienceSelector from "../TriggerForm/SelectRules/AudienceSelector/AudienceSelector";
import TagSelector from "../AssignToTagForm/TagSelector/TagSelector";

const ConditionalSplitForm = (props: { id: string }) => {
  const {
    goBack,
    onSubmit,
    methods,
    formValues,
    control,
    saveRulesDisabled,
    viewMode,
    emailOptions,
    reset,
    getValues,
    audiences,
    handleAudienceSearch,
    loadMoreAudiences,
    isLoadingMore,
    hasMoreResults,
    isLoading,
    isTinyEmail,
    tags,
  } = useConditionalSplitForm(props);

  const ruleTypes = [
    { value: "shopping-activity", label: "Shopping Activity", active: true },
    { value: "marketing-activity", label: "Marketing Activity", active: true },
    { value: "segment-activity", label: "Segment Activity", active: true },
  ];

  const shoppingEventTypes = [
    { value: "CHECKOUT_STARTED", label: "Checkout Started" },
    { value: "ORDER_PLACED", label: "Order Placed" },
    { value: "ORDER_FULFILLED", label: "Order Fulfilled" },
  ];

  const marketingEventTypes = [
    { value: "OPEN", label: "Real-time Open" },
    { value: "CLICK", label: "Real-time Click" },
  ];

  const segmentEventTypes = [
    { value: "EXISTS", label: "Exists in Audience" },
    { value: "EXIST_TAGS", label: "Has Tag" },
  ];

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={onSubmit}
        style={{
          height: "100%",
        }}
      >
        <Box
          sx={{
            py: 4,
            px: 6,
            display: "flex",
            flexDirection: "column",
            width: { xs: "400px", xl: "25vw" },
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 2,
              }}
            >
              <ChevronBigLeft
                sx={{
                  color: "primary.125",
                }}
                onClick={() => goBack()}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Typography variant="h3">Conditional split setup</Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              gap: 8,
              mt: 8,
              flexDirection: "column",
            }}
          >
            {/* Rule Type Selection */}
            <Controller
              name="ruleType"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel
                    sx={(theme) => ({
                      backgroundColor: theme.palette.background.paper,
                      px: 2,
                    })}
                    id="activity-select-label"
                    shrink
                  >
                    Activity Type
                  </InputLabel>
                  <Select
                    size="small"
                    fullWidth
                    label="Activity Type"
                    placeholder="Select activity type"
                    displayEmpty
                    renderValue={(item) => {
                      if (!item?.length) return "Select activity type";
                      return ruleTypes.find((type) => type.value === item)
                        ?.label;
                    }}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      reset({
                        ...getValues(),
                        email: "",
                        event: "",
                        audiences: [],
                      });
                    }}
                  >
                    {ruleTypes.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        disabled={!option.active}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            {formValues.ruleType === "marketing-activity" && (
              <Controller
                name="email"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel
                      sx={(theme) => ({
                        backgroundColor: theme.palette.background.paper,
                        px: 2,
                      })}
                      id="email-select-label"
                      shrink
                    >
                      Email
                    </InputLabel>
                    <Select
                      size="small"
                      fullWidth
                      label="Email"
                      placeholder="Select email"
                      displayEmpty
                      renderValue={(item) => {
                        if (!item?.length) return "Select email";
                        return emailOptions.find(
                          (email) => email.template_id === item
                        )?.name;
                      }}
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        reset({
                          ...getValues(),
                          event: "",
                          links: [],
                        });
                      }}
                    >
                      {emailOptions.map((option) => (
                        <MenuItem
                          key={option.template_id}
                          value={option.template_id}
                        >
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            )}

            {formValues.ruleType === "shopping-activity" && (
              <Controller
                name="event"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel
                      sx={(theme) => ({
                        backgroundColor: theme.palette.background.paper,
                        px: 2,
                      })}
                      id="event-select-label"
                      shrink
                    >
                      Event
                    </InputLabel>
                    <Select
                      size="small"
                      fullWidth
                      label="Event"
                      placeholder="Select event"
                      displayEmpty
                      renderValue={(item) => {
                        if (!item?.length) return "Select event";
                        return shoppingEventTypes.find(
                          (type) => type.value === item
                        )?.label;
                      }}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    >
                      {shoppingEventTypes.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            )}

            {formValues.ruleType === "marketing-activity" &&
              formValues.email && (
                <Controller
                  name="event"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel
                        sx={(theme) => ({
                          backgroundColor: theme.palette.background.paper,
                          px: 2,
                        })}
                        id="marketing-event-select-label"
                        shrink
                      >
                        Event
                      </InputLabel>
                      <Select
                        size="small"
                        fullWidth
                        label="Event"
                        placeholder="Select event"
                        displayEmpty
                        renderValue={(item) => {
                          if (!item?.length) return "Select event";
                          return marketingEventTypes.find(
                            (type) => type.value === item
                          )?.label;
                        }}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        {marketingEventTypes.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              )}

            {formValues.ruleType === "segment-activity" && (
              <>
                <Controller
                  name="event"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel
                        sx={(theme) => ({
                          backgroundColor: theme.palette.background.paper,
                          px: 2,
                        })}
                        id="segment-event-select-label"
                        shrink
                      >
                        Event
                      </InputLabel>
                      <Select
                        size="small"
                        fullWidth
                        label="Event"
                        placeholder="Select event"
                        displayEmpty
                        renderValue={(item) => {
                          if (!item?.length) return "Select event";
                          return segmentEventTypes.find(
                            (type) => type.value === item
                          )?.label;
                        }}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        {segmentEventTypes.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />

                {formValues.event && formValues.event === "EXISTS" && (
                  <Controller
                    name="audiences"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <AudienceSelector
                        value={value || []}
                        onChange={onChange}
                        options={audiences}
                        onInputChange={handleAudienceSearch}
                        isLoading={isLoadingMore || isLoading}
                        hasMore={hasMoreResults}
                        onScrollEnd={loadMoreAudiences}
                        placeholder="Search Audience"
                        label="Audience"
                        enableInfiniteScroll={isTinyEmail}
                      />
                    )}
                  />
                )}

                {formValues.event && formValues.event === "EXIST_TAGS" && (
                  <Controller
                    name="tags"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <TagSelector
                        value={value || []}
                        onChange={onChange}
                        options={tags}
                        placeholder="Search Tags"
                        label="Tags"
                      />
                    )}
                  />
                )}
              </>
            )}

            {formValues.ruleType === "marketing-activity" &&
              formValues.event === "CLICK" && (
                <Controller
                  name="links"
                  control={control}
                  defaultValue={[]}
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel
                        sx={(theme) => ({
                          backgroundColor: theme.palette.background.paper,
                          px: 2,
                        })}
                        shrink
                      >
                        Filter by Links&nbsp;
                        <Box
                          component="span"
                          sx={(theme) => ({
                            fontSize: 10,
                            fontWeight: 600,
                            color: theme.palette.warning.contrastText,
                            background: theme.palette.warning.main,
                            borderRadius: "10px",
                            padding: "2px 8px",
                            lineHeight: "14px",
                            display: "inline-block",
                            verticalAlign: "middle",
                            letterSpacing: 0.3,
                          })}
                        >
                          OPTIONAL
                        </Box>
                      </InputLabel>
                      <Autocomplete<string, true, false, true>
                        multiple
                        options={[] as string[]}
                        value={value || []}
                        freeSolo
                        autoSelect
                        onChange={(_, newValue) => onChange(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Type a URL and press Enter..."
                            size="small"
                            variant="outlined"
                          />
                        )}
                        renderTags={(tagValue, getTagProps) =>
                          tagValue.map((option: string, index: number) => (
                            <Chip
                              label={option}
                              {...getTagProps({ index })}
                              size="small"
                              sx={(theme) => ({
                                background: theme.palette.grey[30],
                                color: theme.palette.grey[90],
                                fontSize: 12,
                                border: 0,
                                fontWeight: 400,
                              })}
                              deleteIcon={<CloseSmall />}
                            />
                          ))
                        }
                      />
                      <Typography
                        variant="caption"
                        sx={(theme) => ({
                          mt: 1,
                          color: theme.palette.text.secondary,
                          fontSize: 11,
                        })}
                      >
                        Only split users who clicked one of these specific
                        links. Leave empty to match any click.
                      </Typography>
                    </FormControl>
                  )}
                />
              )}

            {formValues.event && formValues.ruleType !== "segment-activity" && (
              <>
                <Controller
                  name="from"
                  control={control}
                  defaultValue="start-of-workflow"
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel
                        sx={(theme) => ({
                          backgroundColor: theme.palette.background.paper,
                          px: 2,
                        })}
                        id="from-select-label"
                        shrink
                      >
                        From
                      </InputLabel>
                      <Select
                        size="small"
                        fullWidth
                        label="From"
                        disabled
                        value={field.value}
                      >
                        <MenuItem value="start-of-workflow">
                          Start of Workflow
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />

                {formValues.ruleType === "marketing-activity" &&
                  formValues.email &&
                  formValues.event && (
                    <Box display="flex" flexDirection="column" gap={4}>
                      <Typography variant="h4R">Timeout Settings</Typography>
                      <Controller
                        name="timeoutDays"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth>
                            <InputLabel
                              shrink
                              id="timeout-days-select-label"
                              sx={(theme) => ({
                                backgroundColor: theme.palette.background.paper,
                                pr: 2,
                              })}
                            >
                              Days
                            </InputLabel>
                            <Select
                              size="small"
                              fullWidth
                              value={field.value}
                              onChange={(e: any) => {
                                field.onChange(e.target.value);
                              }}
                              aria-label="timeout-days"
                              label="Days"
                              placeholder="Days"
                              displayEmpty
                              renderValue={(item) => {
                                return item;
                              }}
                            >
                              {[...Array(10)].map((_, index) => {
                                const day = index + 1;
                                return (
                                  <MenuItem
                                    key={`timeout-days-${day}`}
                                    value={day}
                                  >
                                    {day}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Box>
                  )}

                {formValues.ruleType === "marketing-activity" && (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    iconMapping={{
                      info: (
                        <InfoInfo
                          sx={(theme) => ({
                            color: theme.palette.primary[100],
                          })}
                        />
                      ),
                    }}
                  >
                    <Typography variant="p3">
                      When the condition is met, the user immediately moves to
                      the <b>Yes path.</b> The condition is evaluated only for
                      the <b>duration set in the timeout settings</b>. For
                      example, if the timeout is set to 3 days, we'll check for
                      the condition during that period. If the condition isn't
                      met within the timeframe, the user will automatically be
                      sent down the <b>No path.</b>
                    </Typography>
                  </Alert>
                )}

                {formValues.ruleType === "shopping-activity" && (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    iconMapping={{
                      info: (
                        <InfoInfo
                          sx={(theme) => ({
                            color: theme.palette.primary[100],
                          })}
                        />
                      ),
                    }}
                  >
                    <Typography variant="p3">
                      Consider adding a <b>Time Delay node</b> before this
                      condition, as it may take some time for the email to be
                      opened and the click event to be registered. Without a
                      delay, the condition might be evaluated too early and may
                      not produce the expected result.
                    </Typography>
                  </Alert>
                )}
              </>
            )}

            {formValues.ruleType === "segment-activity" &&
              formValues.event === "EXISTS" &&
              formValues.audiences?.length > 0 && (
                <Alert
                  severity="warning"
                  variant="outlined"
                  iconMapping={{
                    info: (
                      <InfoInfo
                        sx={(theme) => ({
                          color: theme.palette.primary[100],
                        })}
                      />
                    ),
                  }}
                >
                  <Typography variant="p3">
                    Users will be split based on their membership in the
                    selected audience(s). If a user exists in any of the
                    selected audiences, they will follow the <b>Yes path</b>,
                    otherwise they will follow the <b>No path</b>.
                  </Typography>
                </Alert>
              )}

            {formValues.ruleType === "segment-activity" &&
              formValues.event === "EXIST_TAGS" &&
              formValues.tags?.length > 0 && (
                <Alert
                  severity="warning"
                  variant="outlined"
                  iconMapping={{
                    info: (
                      <InfoInfo
                        sx={(theme) => ({
                          color: theme.palette.primary[100],
                        })}
                      />
                    ),
                  }}
                >
                  <Typography variant="p3">
                    Users will be split based on whether they have any of the
                    selected tags. If a user has at least one of the selected
                    tags, they will follow the <b>Yes path</b>, otherwise they
                    will follow the <b>No path</b>.
                  </Typography>
                </Alert>
              )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              p: 2,
            }}
          >
            <Button onClick={goBack}>Cancel</Button>
            <Button
              variant="contained"
              type="submit"
              disabled={saveRulesDisabled}
            >
              Save
            </Button>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};

export default ConditionalSplitForm;
